// セクション単位の編集開始/解除と、編集ボタンの表示条件をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createSectionEditStateService(deps) {
    const {
        DicVal,
        EDIT_UPDATE_TRACKING_PANEL_TYPES,
        G_id,
        KAKOUSIJI_ANCHOR_PANEL_TYPE,
        KAKOUSIJI_EDIT_BUTTON_PANEL_TYPES,
        KAKOUSIJI_NONZERO_EDIT_BUTTON_PANEL_TYPES,
        KAKOUSIJI_ZERO_EDIT_BUTTON_PANEL_TYPES,
        activeKakousijiSummaryItem,
        editableSectionsRef,
        entryFormStartedAtRef,
        entrydataRef,
        fnc,
        formstatus,
        isEmpty,
        isEnabled,
        isKakousijiPanel,
        kakousijiSectionEditStartedRef,
        normalizeKakousijiKubunValue,
        orderedPanels,
        resolveCurrentOppiCode,
        restoreScrollPos,
        saveScrollPos,
        sectionButtonRefs,
        sectionEditStartProcessingRef,
        sectionFieldsetRefs,
        sectionHeaderFieldsetRefs,
        sectionOverlayRefs,
        sectionOverwriteConfirmState,
        setSectionEditInfo,
        setSectionEditRenderVersion,
        setSectionOverwriteConfirmState
    } = deps;

    function sectionKey(panel) {
        const displayKey = String(DicVal(panel, 'sampleDisplayKey', '')).trim();
        if (displayKey !== '') return displayKey;
        return `${panel.id}_${isEmpty(panel.type) ? "" : panel.type}`;
    }
    function setSectionRef(refMap, key, node) {
        if (isEmpty(node)) {
            delete refMap.current[key];
            return;
        }
        refMap.current[key] = node;
    }
    function isKakousijiEditButtonPanel(panel) {
        return KAKOUSIJI_EDIT_BUTTON_PANEL_TYPES.includes(String(DicVal(panel, 'type', '')).trim());
    }
    function currentKakousijiEditButtonPanelTypes() {
        return selectedKakousijiKubunValue() === '0' ? KAKOUSIJI_ZERO_EDIT_BUTTON_PANEL_TYPES : KAKOUSIJI_NONZERO_EDIT_BUTTON_PANEL_TYPES;
    }
    function selectedKakousijiKubunValue() {
        return normalizeKakousijiKubunValue(DicVal(activeKakousijiSummaryItem(), 'kubunValue', ''));
    }
    function selectedKakousijiNo() {
        return String(DicVal(activeKakousijiSummaryItem(), 'code', '')).trim();
    }
    function showKakousijiSectionEditButton(panel) {
        const sectionType = String(DicVal(panel, 'type', '')).trim();
        return currentKakousijiEditButtonPanelTypes().includes(sectionType) && !isEmpty(selectedKakousijiNo());
    }
    function isKakousijiSectionEditActive() {
        return !!kakousijiSectionEditStartedRef.current;
    }
    // NEW FORM で編集可/編集不可トグルを表示しないセクション(=常時編集可として扱う)。
    // 対象: 商品情報2/価格情報/基本情報1/基本情報2/物流情報(101-105)、
    //       カラー(200)/サイズ(210)/SKU(220)/セットアイテム(230)。
    // ENTRY FORM では従来どおりトグルで制御する。
    function isNewFormAlwaysEditableSection(panel) {
        if (formstatus !== "NEW FORM") return false;
        const id = String(DicVal(panel, 'id', ''));
        const type = String(DicVal(panel, 'type', '')).trim();
        if (id === "0" && ["101", "102", "103", "104", "105"].includes(type)) return true;
        return (id === "1" && type === "200")
            || (id === "2" && type === "210")
            || (id === "3" && type === "220")
            || (id === "4" && type === "230");
    }
    function sectionEditable(panel) {
        if (isNewFormAlwaysEditableSection(panel)) return true;
        if (isKakousijiPanel(panel) && isKakousijiSectionEditActive()) return true;
        if (showKakousijiSectionEditButton(panel)) return !!editableSectionsRef.current[sectionKey(panel)];
        if (isKakousijiPanel(panel)) return true;
        return !!editableSectionsRef.current[sectionKey(panel)];
    }
    function sectionBodyLocked(panel) {
        if (panel.id === G_id.BASE && String(panel.type) === "100") return false;
        if (showKakousijiSectionEditButton(panel)) return !sectionEditable(panel);
        if (isKakousijiPanel(panel)) return false;
        if (String(DicVal(panel, 'type', '')).trim() === KAKOUSIJI_ANCHOR_PANEL_TYPE) return false;
        return !sectionEditable(panel);
    }
    function setSectionButtonState(panel, editable) {
        const button = sectionButtonRefs.current[sectionKey(panel)];
        if (isEmpty(button)) return;
        if (showKakousijiSectionEditButton(panel)) {
            button.style.backgroundColor = '#42b54f';
            button.style.color = '#ffffff';
            button.style.borderColor = '#42b54f';
            return;
        }
        const label = button.querySelector('.MuiButton-label');
        if (!isEmpty(label)) label.textContent = editable ? "編集可" : "編集不可";
        button.style.backgroundColor = '#2e7d32';
        button.style.color = '#ffffff';
        button.style.borderColor = '#2e7d32';
    }
    function applySectionEditState(panel, editable) {
        const key = sectionKey(panel);
        if (editable) {
            editableSectionsRef.current[key] = true;
        } else {
            delete editableSectionsRef.current[key];
        }
        const bodyLocked = sectionBodyLocked(panel);
        const fieldset = sectionFieldsetRefs.current[key];
        if (!isEmpty(fieldset)) fieldset.disabled = bodyLocked;
        const headerFieldset = sectionHeaderFieldsetRefs.current[key];
        if (!isEmpty(headerFieldset)) headerFieldset.disabled = bodyLocked;
        const overlay = sectionOverlayRefs.current[key];
        if (!isEmpty(overlay)) overlay.style.display = bodyLocked ? '' : 'none';
        setSectionButtonState(panel, editable);
    }
    function applySectionEditToggle(panel) {
        saveScrollPos();
        if (!sectionBodyLocked(panel) && document.activeElement && typeof document.activeElement.blur === "function") {
            document.activeElement.blur();
        }
        applySectionEditState(panel, !sectionEditable(panel));
        setSectionEditRenderVersion((prev) => prev + 1);
        window.requestAnimationFrame(() => {
            restoreScrollPos();
        });
    }
    function applyKakousijiSectionEditStart() {
        saveScrollPos();
        kakousijiSectionEditStartedRef.current = true;
        orderedPanels().forEach((p) => {
            if (currentKakousijiEditButtonPanelTypes().includes(String(DicVal(p, 'type', '')).trim())) applySectionEditState(p, true);
        });
        setSectionEditRenderVersion((prev) => prev + 1);
        window.requestAnimationFrame(() => {
            restoreScrollPos();
        });
    }
    function isEditTrackedSectionPanel(panel) {
        return EDIT_UPDATE_TRACKING_PANEL_TYPES.includes(String(DicVal(panel, "type", "")).trim());
    }
    function beginTrackedSectionEdit(panel, force) {
        const oppiCode = resolveCurrentOppiCode();
        if (isEmpty(oppiCode)) {
            applySectionEditToggle(panel);
            return;
        }
        if (sectionEditStartProcessingRef.current) return;
        sectionEditStartProcessingRef.current = true;
        fnc.current.postaxios("hinbanmaster/BeginSection101Edit",
            {
                OppiCode: oppiCode,
                SectionType: String(DicVal(panel, "type", "")).trim(),
                KakousijiNo: showKakousijiSectionEditButton(panel) ? selectedKakousijiNo() : "",
                EntryStartedAt: entryFormStartedAtRef.current,
                // データ取得時点(初回ロード/前回編集開始時に返却された値)の更新日時。
                // サーバはこれとDB現在値の一致で他者更新を判定する。
                KnownUpdate: DicVal(buildSectionEditInfo(entrydataRef.current, String(DicVal(panel, "type", "")).trim()), "update", ""),
                Force: isEnabled(force)
            },
            {
                onSuccess: function (data) {
                    sectionEditStartProcessingRef.current = false;
                    const returnData = DicVal(data, "returnData", {});
                    if (isEnabled(DicVal(returnData, "RequiresConfirm", false))) {
                        setSectionOverwriteConfirmState({ open: true, panel: panel });
                        return;
                    }
                    if (!isEmpty(entrydataRef.current)) {
                        entrydataRef.current.PDT_UPDATE = DicVal(returnData, "PDT_UPDATE", DicVal(entrydataRef.current, "PDT_UPDATE", ""));
                        entrydataRef.current.PDT_UPDATEAC = DicVal(returnData, "PDT_UPDATEAC", DicVal(entrydataRef.current, "PDT_UPDATEAC", ""));
                        entrydataRef.current.PDTMD_UPDATE = DicVal(returnData, "PDTMD_UPDATE", DicVal(entrydataRef.current, "PDTMD_UPDATE", ""));
                        entrydataRef.current.PDTMD_UPDATEAC = DicVal(returnData, "PDTMD_UPDATEAC", DicVal(entrydataRef.current, "PDTMD_UPDATEAC", ""));
                        entrydataRef.current.PDTC_UPDATE = DicVal(returnData, "PDTC_UPDATE", DicVal(entrydataRef.current, "PDTC_UPDATE", ""));
                        entrydataRef.current.PDTC_UPDATEAC = DicVal(returnData, "PDTC_UPDATEAC", DicVal(entrydataRef.current, "PDTC_UPDATEAC", ""));
                        entrydataRef.current.PDTN_UPDATE = DicVal(returnData, "PDTN_UPDATE", DicVal(entrydataRef.current, "PDTN_UPDATE", ""));
                        entrydataRef.current.PDTN_UPDATEAC = DicVal(returnData, "PDTN_UPDATEAC", DicVal(entrydataRef.current, "PDTN_UPDATEAC", ""));
                        Object.keys(isEmpty(returnData) ? {} : returnData).forEach((key) => {
                            if (/(_UPDATE|_UPDATEAC)$/i.test(key) || String(key).indexOf('KAKOUSIJI_') === 0) {
                                entrydataRef.current[key] = DicVal(returnData, key, DicVal(entrydataRef.current, key, ""));
                            }
                        });
                        if (showKakousijiSectionEditButton(panel)) {
                            KAKOUSIJI_EDIT_BUTTON_PANEL_TYPES.forEach((type) => setSectionEditInfoFromData(entrydataRef.current, type));
                        } else {
                            setSectionEditInfoFromData(entrydataRef.current, String(DicVal(panel, "type", "")).trim());
                        }
                    }
                    if (showKakousijiSectionEditButton(panel)) {
                        applyKakousijiSectionEditStart();
                    } else {
                        applySectionEditToggle(panel);
                    }
                },
                onError: function (data) {
                    sectionEditStartProcessingRef.current = false;
                    let msg = "編集開始処理に失敗しました";
                    if (!isEmpty(data.errDescription)) {
                        msg = data.errDescription;
                    }
                    fnc.current.warnmsg(msg);
                }
            }, true
        );
    }
    function toggleSectionEdit(panel) {
        if (isEditTrackedSectionPanel(panel) && sectionBodyLocked(panel)) {
            beginTrackedSectionEdit(panel, false);
            return;
        }
        applySectionEditToggle(panel);
    }
    function closeSectionOverwriteConfirmDialog() {
        setSectionOverwriteConfirmState({ open: false, panel: null });
    }
    function handleSectionOverwriteConfirmOk() {
        const panel = sectionOverwriteConfirmState.panel;
        closeSectionOverwriteConfirmDialog();
        if (!isEmpty(panel)) beginTrackedSectionEdit(panel, true);
    }
    function resetSectionEdit() {
        editableSectionsRef.current = {};
        kakousijiSectionEditStartedRef.current = false;
        orderedPanels().forEach((p) => applySectionEditState(p, false));
    }
    function formatDateTimeForServer(value) {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }
    function normalizeSectionEditTime(value) {
        return String(isEmpty(value) ? "" : value).trim().replace(/-/g, "/");
    }
    function sectionEditTimeValue(value) {
        const normalized = normalizeSectionEditTime(value);
        if (isEmpty(normalized)) return 0;
        const timestamp = new Date(normalized).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
    }
    function buildSectionEditInfo(data, sectionType) {
        const normalizedSectionType = String(isEmpty(sectionType) ? "" : sectionType).trim();
        let candidates = [
            { update: normalizeSectionEditTime(DicVal(data, "PDT_UPDATE", "")), user: String(DicVal(data, "PDT_UPDATEAC", "")).trim() },
            { update: normalizeSectionEditTime(DicVal(data, "PDTMD_UPDATE", "")), user: String(DicVal(data, "PDTMD_UPDATEAC", "")).trim() }
        ];
        if (normalizedSectionType === "103") {
            candidates = [
                ...candidates,
                { update: normalizeSectionEditTime(DicVal(data, "PDTN_UPDATE", "")), user: String(DicVal(data, "PDTN_UPDATEAC", "")).trim() }
            ];
        }
        if (KAKOUSIJI_EDIT_BUTTON_PANEL_TYPES.includes(normalizedSectionType)) {
            candidates = [
                { update: normalizeSectionEditTime(DicVal(data, 'KAKOUSIJI_' + normalizedSectionType + '_UPDATE', "")), user: String(DicVal(data, 'KAKOUSIJI_' + normalizedSectionType + '_UPDATEAC', "")).trim() },
                { update: normalizeSectionEditTime(DicVal(data, "KAKOUSIJI_UPDATE", "")), user: String(DicVal(data, "KAKOUSIJI_UPDATEAC", "")).trim() },
                { update: normalizeSectionEditTime(DicVal(data, "SSD_UPDATE", "")), user: String(DicVal(data, "SSD_UPDATEAC", "")).trim() }
            ];
        } else if (["200", "210", "220"].includes(normalizedSectionType)) {
            candidates = [
                { update: normalizeSectionEditTime(DicVal(data, "PDTC_UPDATE", "")), user: String(DicVal(data, "PDTC_UPDATEAC", "")).trim() }
            ];
        } else if (["104", "105", "230"].includes(normalizedSectionType)) {
            candidates = [
                { update: normalizeSectionEditTime(DicVal(data, "PDT_UPDATE", "")), user: String(DicVal(data, "PDT_UPDATEAC", "")).trim() }
            ];
        }
        const latest = candidates.reduce((current, candidate) => {
            return sectionEditTimeValue(candidate.update) > sectionEditTimeValue(current.update) ? candidate : current;
        }, { user: "", update: "" });
        return { user: latest.user, update: latest.update };
    }
    // 複数セクションぶんを1回の state 更新にまとめる。
    // 個別呼び出しを24回並べると React 17 ではイベント外で毎回同期レンダーが走るため。
    function setSectionEditInfoFromDataBulk(data, sectionTypes) {
        const types = (Array.isArray(sectionTypes) ? sectionTypes : [])
            .map((sectionType) => String(isEmpty(sectionType) ? "" : sectionType).trim())
            .filter((sectionType) => sectionType !== "");
        if (types.length === 0) return;
        const next = {};
        types.forEach((sectionType) => { next[sectionType] = buildSectionEditInfo(data, sectionType); });
        setSectionEditInfo((prev) => ({ ...(isEmpty(prev) ? {} : prev), ...next }));
    }
    function setSectionEditInfoFromData(data, sectionType) {
        const normalizedSectionType = String(isEmpty(sectionType) ? "" : sectionType).trim();
        if (isEmpty(normalizedSectionType)) return;
        setSectionEditInfo((prev) => ({
            ...(isEmpty(prev) ? {} : prev),
            [normalizedSectionType]: buildSectionEditInfo(data, normalizedSectionType)
        }));
    }

    return {
        sectionKey,
        setSectionRef,
        isKakousijiEditButtonPanel,
        currentKakousijiEditButtonPanelTypes,
        selectedKakousijiKubunValue,
        selectedKakousijiNo,
        showKakousijiSectionEditButton,
        isKakousijiSectionEditActive,
        sectionEditable,
        sectionBodyLocked,
        setSectionButtonState,
        applySectionEditState,
        applySectionEditToggle,
        applyKakousijiSectionEditStart,
        isEditTrackedSectionPanel,
        beginTrackedSectionEdit,
        toggleSectionEdit,
        closeSectionOverwriteConfirmDialog,
        handleSectionOverwriteConfirmOk,
        resetSectionEdit,
        formatDateTimeForServer,
        normalizeSectionEditTime,
        sectionEditTimeValue,
        buildSectionEditInfo,
        setSectionEditInfoFromData,
        setSectionEditInfoFromDataBulk
    };
}
